/* eslint-disable max-depth */
import type { ExtendedEntityParameters } from 'src/components/EntityDetail';
import { type ValidationResult } from 'types';
import { AddContextDocument } from 'types/db';
import { type ArgoAuthProviderLocalSetting } from '@redviking/argonaut-util/types/db-schema/argo/auth_provider_local_setting';
import { type ArgoAuthProviderMsalSetting } from '@redviking/argonaut-util/types/db-schema/argo/auth_provider_msal_setting';
import { type ArgoUserRoleBoolExp, type ArgoUserRoleInsertInput } from '@redviking/argonaut-util/types/db-schema/argo/user_role';
import { validateContext } from './context.validations';
import { gqlClient } from 'src/util/gql-client';

export type ContextEntity = {
    id: string;
    name: string;
    enabled: boolean;
    created_at: string;
    description: string;
    users: {
        userId: string;
        roleIds: string[];
    }[];
    authSettings: {
        msal: Pick<ArgoAuthProviderMsalSetting, 'enabled' | 'config'>;
        local: Pick<ArgoAuthProviderLocalSetting, 'enabled' | 'config'>;
    };
};

export const saveContext = async (payload: ExtendedEntityParameters<'context'>): Promise<ValidationResult> => {
    const { entity, oldEntity } = payload;
    const validationResult = await validateContext(entity, oldEntity);
    if (validationResult.status !== 'success') {
        return validationResult;
    }
    try {

        const userRolesToBeDeleted: {
            roleId: string;
            userId: string;
            contextId: string;
        }[] = [];
        for (const oldUser of oldEntity?.users || []) {
            const existingUser = entity.users.find(u => u.userId === oldUser.userId);
            if (existingUser) {
                for (const oldUserRoleId of oldUser.roleIds) {
                    const existingUserRoleId = existingUser.roleIds.find(roleId => roleId === oldUserRoleId);
                    if (!existingUserRoleId) {
                        userRolesToBeDeleted.push({
                            contextId: entity.id,
                            roleId: oldUserRoleId,
                            userId: oldUser.userId,
                        });
                    }
                }
            } else {
                for (const oldUserRoleId of oldUser.roleIds) {
                    userRolesToBeDeleted.push({
                        contextId: entity.id,
                        roleId: oldUserRoleId,
                        userId: oldUser.userId,
                    });
                }
            }
        }

        await gqlClient.request({
            document: AddContextDocument,
            variables: {
                argoAuthProviderLocalSetting: {
                    context_id: entity.id,
                    auth_provider_id: 'local',
                    config: entity.authSettings.local.config,
                    enabled: entity.authSettings.local.enabled,
                    created_by_id: 'b8d5420c-691c-11ec-831b-9e3d90d063e7',
                },
                argoAuthProviderMsalSetting: {
                    context_id: entity.id,
                    auth_provider_id: 'msal',
                    config: entity.authSettings.msal.config,
                    enabled: entity.authSettings.msal.enabled,
                    created_by_id: 'b8d5420c-691c-11ec-831b-9e3d90d063e7',
                },
                context: {
                    id: entity.id,
                    name: entity.name,
                    enabled: entity.enabled,
                    created_at: entity.created_at,
                    description: entity.description,
                },
                contextUserRoles: entity.users.flatMap<ArgoUserRoleInsertInput>(i => i.roleIds.map<ArgoUserRoleInsertInput>(roleId => ({
                    role_id: roleId,
                    user_id: i.userId,
                    context_id: entity.id,
                }))),
                deleteArgoUserRoles: {
                    _or: userRolesToBeDeleted.map<ArgoUserRoleBoolExp>(i => ({
                        role_id: {
                            _eq: i.roleId,
                        },
                        user_id: {
                            _eq: i.userId,
                        },
                        context_id: {
                            _eq: i.contextId,
                        },
                    })),
                },
            },
        });

        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Context: "${JSON.stringify(err)}"`,
        };
    }
};
